import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  BadgeCheck,
  Briefcase,
  Building2,
  CircleAlert,
  ClipboardList,
  Contact,
  ShieldCheck,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import OnboardingLayout from "@/components/layout/OnboardingLayout";
import { useAuthStore } from "@/store/authStore";
import { useIndustries, useRegions, useSkills } from "@/hooks/useReferenceData";
import apiClient, { setAuthToken } from "@/lib/api-client";
import { Field } from "@/components/forms";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiCombobox } from "@/components/ui/multi-combobox";
import {
  DocumentUploadRow,
  OnboardingComplete,
  StepHeader,
} from "@/components/onboarding/OnboardingParts";

export const Route = createFileRoute("/onboarding/partner")({
  component: PartnerOnboarding,
});

const steps = ["Organization", "Contact", "Hiring", "Identity"];

function PartnerOnboarding() {
  const [activeStep, setActiveStep] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [files, setFiles] = useState<Record<string, File | null>>({});
  const { user, setUser } = useAuthStore();
  const navigate = useNavigate();

  const { data: regions } = useRegions();
  const { data: industriesData } = useIndustries();
  const { data: skillsData } = useSkills();

  const [formData, setFormData] = useState({
    name: "",
    regNo: "",
    industry: "",
    website: "",
    contactName: "",
    email: "",
    phone: "",
    region: "",
    capacity: "",
    selectedSkills: [] as string[],
    environment: [] as string[],
  });

  const calculateProgress = () => {
    const totalFields = 12;
    let filledFields = 0;
    if (formData.name) filledFields++;
    if (formData.regNo) filledFields++;
    if (formData.industry) filledFields++;
    if (formData.contactName) filledFields++;
    if (formData.email) filledFields++;
    if (formData.phone) filledFields++;
    if (formData.region) filledFields++;
    if (formData.capacity) filledFields++;
    if (formData.selectedSkills.length > 0) filledFields++;
    if (formData.environment.length > 0) filledFields++;
    if (files["BRELA Registration Certificate"]) filledFields++;
    if (files["TIN / VAT Verification"]) filledFields++;
    return (filledFields / totalFields) * 100;
  };

  const validateStep = () => {
    if (activeStep === 0) {
      if (!formData.name || !formData.regNo || !formData.industry) return false;
    } else if (activeStep === 1) {
      if (!formData.contactName || !formData.email || !formData.phone || !formData.region)
        return false;
    } else if (activeStep === 2) {
      if (!formData.capacity || formData.selectedSkills.length === 0) return false;
    }
    return true;
  };

  const handleNext = async () => {
    if (!validateStep()) {
      alert("Please fill in all required fields before proceeding.");
      return;
    }

    if (activeStep === steps.length - 1) {
      setLoading(true);
      setErrorMsg(null);
      try {
        // 1. Update Partner Profile (also promotes the account to `partner`)
        await apiClient.patch("/partners/me", {
          company_name: formData.name,
          industry: formData.industry,
          website: formData.website,
          location: formData.region,
          contact_person: formData.contactName,
          contact_email: formData.email,
          contact_phone: formData.phone,
          description: `Capacity: ${formData.capacity} slots. Skills: ${formData.selectedSkills.join(", ")}. Environment: ${formData.environment.join(", ")}`,
        });

        // 2. Upload Documents
        const uploadDoc = async (file: File) => {
          const form = new FormData();
          form.append("file", file);
          await apiClient.post("/partners/me/documents", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        };

        const regCert = files["BRELA Registration Certificate"];
        const tinCert = files["TIN / VAT Verification"];

        if (regCert) await uploadDoc(regCert);
        if (tinCert) await uploadDoc(tinCert);

        // 3. Refresh session BEFORE creating a slot — the token still carries the
        // old `unassigned` role, and /partners/me/slots requires `partner`.
        let sessionRefreshed = false;
        try {
          const refreshRes = await apiClient.post("/auth/refresh");
          const newToken = refreshRes.data.data.token;
          if (newToken) setAuthToken(newToken);

          const meRes = await apiClient.get("/auth/me");
          setUser(meRes.data.data);
          sessionRefreshed = true;
        } catch (refreshErr: unknown) {
          console.error("Failed to refresh session:", refreshErr);
          if (user) {
            setUser({
              ...user,
              role: "partner",
              status: "active",
              company: formData.name,
            });
          }
        }

        // 4. Create initial slot. Not critical to onboarding — never block on it.
        if (sessionRefreshed && formData.capacity && parseInt(formData.capacity) > 0) {
          try {
            await apiClient.post(
              "/partners/me/slots",
              {
                title: `${formData.name} - Internship Placement`,
                description: `Internship position at ${formData.name}. Required skills: ${formData.selectedSkills.join(", ")}. Work environment: ${formData.environment.join(", ")}.`,
                max_students: parseInt(formData.capacity),
                requirements: formData.selectedSkills.join(", "),
                arrangement: formData.environment.includes("Remote")
                  ? "remote"
                  : formData.environment.includes("On-site")
                    ? "on-site"
                    : "hybrid",
              },
              { skipGlobalErrorHandler: true } as any,
            );
          } catch (slotErr: unknown) {
            console.error("Failed to create initial slot:", slotErr);
          }
        }

        setSubmitted(true);
      } catch (err: unknown) {
        console.error("Partner onboarding failed:", err);
        const message =
          (err as any)?.response?.data?.message ||
          (err as any)?.message ||
          "Something went wrong while finalizing your onboarding.";
        setErrorMsg(`${message} Please try again.`);
      } finally {
        setLoading(false);
      }
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };


  const stepIcons = [<Building2 />, <Contact />, <Briefcase />, <ShieldCheck />];

  const skillOptions = (skillsData || []).map((s: string | { id: number; name: string }) => {
    const name = typeof s === "string" ? s : s.name;
    return { value: name, label: name };
  });

  if (submitted) {
    return (
      <OnboardingLayout
        title="Partnership Established"
        subtitle="Your organization profile has been submitted for institutional verification."
      >
        <OnboardingComplete
          title="Welcome to the Network!"
          body="Your corporate profile is now undergoing institutional verification. You can proceed to the dashboard to begin configuring your placement slots."
          status="Verification typically takes 24-48 hours"
          to="/partner/dashboard"
          cta="Launch Partner Dashboard"
        />
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout
      step={activeStep}
      totalSteps={steps.length}
      fieldProgress={calculateProgress()}
      title="Partner Calibration"
      subtitle="Establish your corporate presence and connect with university talent."
    >
      <div className="w-full">
        <StepHeader
          icon={stepIcons[activeStep]}
          title={steps[activeStep]}
          description={
            activeStep === 0
              ? "Provide your organization details."
              : activeStep === 1
                ? "Primary contact for placement coordination."
                : activeStep === 2
                  ? "Define your technical talent requirements."
                  : "Compliance and official verification."
          }
        />

        <div className="min-h-[340px] w-full text-left">
          {activeStep === 0 && (
            <div className="flex flex-col gap-4">
              <Field label="Organization Name" required>
                <Input
                  placeholder="e.g. CRDB Bank PLC"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Registration No. (BRELA)" required>
                  <Input
                    placeholder="e.g. 123456"
                    value={formData.regNo}
                    onChange={(e) => setFormData((p) => ({ ...p, regNo: e.target.value }))}
                    required
                  />
                </Field>
                <Field label="Industrial Sector" required>
                  {({ id, describedBy, invalid }) => (
                    <Select
                      value={formData.industry}
                      onValueChange={(v) => setFormData((p) => ({ ...p, industry: v }))}
                    >
                      <SelectTrigger
                        id={id}
                        aria-describedby={describedBy}
                        aria-invalid={invalid || undefined}
                      >
                        <SelectValue placeholder="Select industry" />
                      </SelectTrigger>
                      <SelectContent>
                        {industriesData?.map((i: string | { id: number; name: string }) => (
                          <SelectItem
                            key={typeof i === "string" ? i : i.id}
                            value={typeof i === "string" ? i : i.name}
                          >
                            {typeof i === "string" ? i : i.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              </div>

              <Field label="Corporate Website">
                <Input
                  type="url"
                  placeholder="https://www.organization.co.tz"
                  value={formData.website}
                  onChange={(e) => setFormData((p) => ({ ...p, website: e.target.value }))}
                />
              </Field>
            </div>
          )}

          {activeStep === 1 && (
            <div className="flex flex-col gap-4">
              <Field label="Focal Point Representative" required>
                <Input
                  placeholder="Full legal name"
                  value={formData.contactName}
                  onChange={(e) => setFormData((p) => ({ ...p, contactName: e.target.value }))}
                  required
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Official Email" required>
                  <Input
                    type="email"
                    placeholder="hr@organization.co.tz"
                    value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                    required
                  />
                </Field>
                <Field label="Direct Line" required>
                  <Input
                    type="tel"
                    placeholder="+255 7XX XXX XXX"
                    value={formData.phone}
                    onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                    required
                  />
                </Field>
              </div>

              <Field label="Operating Region" required>
                {({ id, describedBy, invalid }) => (
                  <Select
                    value={formData.region}
                    onValueChange={(v) => setFormData((p) => ({ ...p, region: v }))}
                  >
                    <SelectTrigger
                      id={id}
                      aria-describedby={describedBy}
                      aria-invalid={invalid || undefined}
                    >
                      <SelectValue placeholder="Select region" />
                    </SelectTrigger>
                    <SelectContent>
                      {regions?.map((r: string) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
          )}

          {activeStep === 2 && (
            <div className="flex flex-col gap-4">
              <Field label="Placement capacity" required>
                <Input
                  type="number"
                  min={0}
                  inputMode="numeric"
                  placeholder="Number of available slots"
                  value={formData.capacity}
                  onChange={(e) => setFormData((p) => ({ ...p, capacity: e.target.value }))}
                />
              </Field>

              <Field label="Technical skill requirements" required>
                {({ id, describedBy, invalid }) => (
                  <MultiCombobox
                    id={id}
                    aria-describedby={describedBy}
                    aria-invalid={invalid || undefined}
                    options={skillOptions}
                    value={formData.selectedSkills}
                    onChange={(names) => setFormData((p) => ({ ...p, selectedSkills: names }))}
                    placeholder="Select expertise"
                    searchPlaceholder="Search skills…"
                    emptyText="No skills found."
                  />
                )}
              </Field>

              <fieldset className="mt-1">
                <legend className="mb-2.5 text-sm font-medium text-foreground">
                  Working Environment
                </legend>
                <div className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-2">
                  {["Remote", "On-site", "Hybrid Policy", "Flexible Scheduling"].map((env) => {
                    const checkboxId = `env-${env.replace(/\s+/g, "-").toLowerCase()}`;
                    return (
                      <div
                        key={env}
                        className="flex items-center gap-2.5 rounded-sm border border-border bg-card px-3 py-2.5"
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={formData.environment.includes(env)}
                          onCheckedChange={(checked) => {
                            const next = checked
                              ? [...formData.environment, env]
                              : formData.environment.filter((x) => x !== env);
                            setFormData((p) => ({ ...p, environment: next }));
                          }}
                        />
                        <Label htmlFor={checkboxId} className="flex-1 font-normal text-body-strong">
                          {env}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </fieldset>
            </div>
          )}

          {activeStep === 3 && (
            <div className="flex flex-col gap-3">
              <p className="mb-1 text-sm text-body">
                Please upload clear digital copies of your organization's legal documentation for
                verification purposes.
              </p>
              {[
                { label: "BRELA Registration Certificate", icon: <ClipboardList /> },
                { label: "TIN / VAT Verification", icon: <BadgeCheck /> },
              ].map((doc) => (
                <DocumentUploadRow
                  key={doc.label}
                  label={doc.label}
                  hint="PDF, JPG or PNG (Max 10MB)"
                  icon={doc.icon}
                  file={files[doc.label]}
                  onSelect={(f) => setFiles((p) => ({ ...p, [doc.label]: f }))}
                  onClear={() => setFiles((p) => ({ ...p, [doc.label]: null }))}
                />
              ))}
            </div>
          )}
        </div>

        {errorMsg && (
          <Alert variant="destructive" className="mt-6">
            <CircleAlert />
            <AlertDescription>{errorMsg}</AlertDescription>
          </Alert>
        )}

        <div className="mt-8 flex w-full gap-2">
          <Button
            variant="outline"
            size="lg"
            disabled={loading}
            onClick={() =>
              activeStep === 0 ? navigate({ to: "/onboarding" }) : setActiveStep((p) => p - 1)
            }
            className="min-w-[120px]"
          >
            Go back
          </Button>
          <Button size="lg" onClick={handleNext} loading={loading} className="flex-1">
            {loading
              ? "Finalizing…"
              : activeStep === steps.length - 1
                ? "Finalize Onboarding"
                : "Continue"}
          </Button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
